import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  getSubContractDetail,
  updateSubContract,
} from "../../api/contract/sub";
import CompanySearch from "../../components/contract/CompanySearch";
import TableCell from "../../components/common/ui/TableCell";
import SidePanel from "../../components/common/layout/SidePanel";
import AttachmentBox from "../../components/common/papers/AttachmentBox";
import FileList from "../../components/common/papers/FileList";
import ActivitySearch from "../../components/const/Photo/ActivitySearch";
import { softDeleteDoc } from "../../api/papers";

// 기본 메타 구조
const emptyMeta = {
  title: "",
  contract_code: "",
  confirmer_company_name: "",
  revision: 1,
  version: 1,
  construction_type: "",
  construction_type_detail: "",
  status: "",
  amount: "",
  memo: "",
  activity_ids: [],
};

const TYPE_DETAIL_LABELS = {
  GENERAL: "General (일반)",
  ARCHITECTURAL: "Architectural (건축)",
  STRUCTURAL: "Structural (구조)",
  CIVIL: "Civil (토목)",
  LANDSCAPING: "Landscaping (조경)",
  MECHANICAL: "Mechanical (기계)",
  ELECTRICAL: "Electrical (전기)",
  COMMUNICATION: "Communication (통신)",
  FIREFIGHTING: "Firefighting (소방)",
  INTERIOR: "Interior (인테리어)",
  OTHERS: "기타",
};

const STATUS_LABELS = {
  DRAFT: "Draft (임시)",
  FOR_INFORMATION: "For Information (정보)",
  FOR_REVIEW: "For Review (검토)",
  FOR_APPROVAL: "For Approval (승인)",
  FINAL: "Final (최종)",
  AS_BUILT: "As-Built (준공)",
  NO_LONGER_IN_USE: "No Longer In Use (폐기)",
};

// 데이터 정규화
const normalize = (data) => ({
  dns: data?.dns ?? "",
  created_at: data?.created_at ?? null,
  created_by: data?.created_by ?? "",
  versions: data?.versions ?? [],
  status_history: data?.status_history ?? [],
  meta: { ...emptyMeta, ...(data?.meta ?? {}) },
});

const Sub_view = ({ contractId: propId, onDeleted, onUpdated, onClose }) => {
  const { contractId: routeId } = useParams();
  const contractId = propId || routeId;

  const [modalOpen, setModalOpen] = useState(false);
  const [contract, setContract] = useState(() => normalize({}));
  const [form, setForm] = useState(() => normalize({}));
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const [loadedFiles, setLoadedFiles] = useState([]);
  const attachmentRef = useRef(null);

  const [showVersionPanel, setShowVersionPanel] = useState(false);

  /* ---------------------- 데이터 불러오기 ---------------------- */
  const fetchDetail = useCallback(
    async (id = contractId) => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await getSubContractDetail(id);
        const norm = normalize(data);
        setContract(norm);
        setForm(norm);
      } finally {
        setLoading(false);
      }
    },
    [contractId]
  );

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  /* ------------------------- 삭제 ------------------------- */
  const handleDelete = async () => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    // 모든 버전 DNS 삭제
    const dnsList = [contract.dns, ...contract.versions.map((v) => v.dns)];

    await Promise.all(dnsList.map((dns) => softDeleteDoc(dns, true)));

    alert("삭제되었습니다.");
    onDeleted?.();
  };

  /* -------------------- 변경 핸들링 --------------------- */
  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, meta: { ...p.meta, [name]: value } }));
  };

  /* -------------------------- 저장 -------------------------- */
  const handleSave = async () => {
    const fd = new FormData();
    fd.append("title", form.meta.title);
    fd.append("meta", JSON.stringify(form.meta));

    const res = await updateSubContract(contractId, fd);

    await attachmentRef.current?.save(res.dns);

    alert("수정되었습니다.");
    setEditing(false);
    fetchDetail();
    onUpdated?.(res.dns);
  };

  if (loading) return <div className="p-6 text-center">로딩 중...</div>;

  const meta = contract.meta;
  const versions = contract.versions;

  return (
    <div className="p-6 space-y-8 bg-white rounded-xl shadow">
      {/* -------------------- 헤더 버튼 / 버전 -------------------- */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        {/* 버전 버튼 */}
        <button
          onClick={() => setShowVersionPanel(true)}
          className="px-4 py-2 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm"
        >
          Rev.{meta.revision} v{meta.version}{" "}
          {meta.is_current ? "(최신)" : "(과거)"}
        </button>

        {/* 수정/삭제 버튼 */}
        <div className="flex gap-2">
          {editing ? (
            <>
              <button className="btn-save" onClick={handleSave}>
                저장
              </button>
              <button
                className="btn-cancel"
                onClick={() => {
                  setEditing(false);
                  setForm(contract);
                }}
              >
                취소
              </button>
            </>
          ) : (
            meta.is_current && (
              <button onClick={() => setEditing(true)} className="btn-edit">
                수정
              </button>
            )
          )}

          {meta.is_current && (
            <button onClick={handleDelete} className="btn-delete">
              삭제
            </button>
          )}

          {onClose && (
            <button className="btn-cancel" onClick={onClose}>
              닫기
            </button>
          )}
        </div>
      </div>

      {/* -------------------- 상태 변경 이력 -------------------- */}
      {contract.status_history?.length > 0 && (
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <h3 className="font-semibold mb-3">상태 변경 이력</h3>
          <div className="flex flex-wrap items-center gap-4">
            {contract.status_history.map((h, idx) => {
              const active = idx === contract.status_history.length - 1;
              return (
                <div key={idx} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${active
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700"
                      }`}
                  >
                    {idx + 1}
                  </div>
                  <div className="ml-2">
                    <div className="font-medium">{STATUS_LABELS[h.status]}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(h.entered_at).toLocaleDateString("ko-KR")}
                      {h.exited_at &&
                        ` ~ ${new Date(h.exited_at).toLocaleDateString(
                          "ko-KR"
                        )}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* -------------------- 계약 정보 -------------------- */}
      <div className="overflow-x-auto ">
        <table className="min-w-full text-sm">
          <colgroup>
            <col />
            <col style={{ width: "380px" }} />
          </colgroup>
          <tbody className="divide-y">
            {/* 계약명 */}
            <tr>
              <TableCell
                label="계약명"
                editing={editing}
                value={meta.title}
                input={
                  <input
                    name="title"
                    value={form.meta.title}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />

              <TableCell
                label="수정일"
                value={
                  contract.created_at
                    ? new Date(contract.created_at)
                        .toISOString()
                        .slice(0, 10)
                        .replace(/-/g, ". ")
                    : "-"
                }
              />
            </tr>

            {/* 코드 / 업체명 */}
            <tr>
              <TableCell
                label="계약 코드"
                editing={editing}
                value={meta.contract_code}
                input={
                  <input
                    name="contract_code"
                    value={form.meta.contract_code}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />
              <TableCell
                label="공종"
                editing={editing}
                value={meta.construction_type}
                input={
                  <div>
                    <input
                      type="text"
                      name="construction_type"
                      value={form.meta.construction_type}
                      onChange={handleMetaChange}
                      className="w-full border rounded-md px-3 py-2"
                      placeholder="공종을 입력하세요"
                    />
                  </div>
                }
                // input={
                //   <select
                //     name="construction_type"
                //     className="input-base"
                //     value={form.meta.construction_type}
                //     onChange={handleMetaChange}
                //   >
                //     <option value="본공사">본공사</option>
                //     <option value="전기공사">전기공사</option>
                //     <option value="통신공사">통신공사</option>
                //     <option value="소방공사">소방공사</option>
                //   </select>
                // }
              />
            </tr>

            {/* Revision / 공종 */}
            <tr>
              <TableCell
                label="상태"
                editing={editing}
                value={STATUS_LABELS[meta.status]}
                input={
                  <select
                    name="status"
                    value={form.meta.status}
                    onChange={handleMetaChange}
                    className="input-base"
                  >
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                }
              />
              <TableCell
                label="금액"
                editing={editing}
                value={`${Number(meta.amount ?? 0).toLocaleString("ko-KR")} 원`}
                input={
                  <input
                    type="number"
                    name="amount"
                    value={form.meta.amount}
                    onChange={handleMetaChange}
                    className="input-base text-right"
                  />
                }
              />
            </tr>

            {/* 금액 / 작성자 */}
            <tr>
              <TableCell
                label="주계약업체"
                editing={editing}
                value={meta.confirmer_company_name}
                input={
                  <div className="flex items-center gap-2 w-full">
                    {/* 직접입력 체크 */}
                    <label className="flex items-center gap-1 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={form.meta.manualCompanyInput}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            meta: {
                              ...form.meta,
                              manualCompanyInput: e.target.checked,
                              confirmer_company_name: "",
                            },
                          })
                        }
                      />
                      <span className="text-sm">직접 입력</span>
                    </label>

                    {/* 입력 UI */}
                    {form.meta.manualCompanyInput ? (
                      /* 직접 입력 */
                      <input
                        type="text"
                        name="confirmer_company_name"
                        value={form.meta.confirmer_company_name}
                        onChange={handleMetaChange}
                        className="input-base flex-1"
                        placeholder="업체명 입력"
                      />
                    ) : (
                      /* 검색 선택 */
                      <div className="flex flex-1 gap-2">
                        <input
                          readOnly
                          value={form.meta.confirmer_company_name}
                          placeholder="업체 선택"
                          className="input-base bg-gray-100 flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => setModalOpen(true)}
                          className="btn-edit px-3"
                        >
                          검색
                        </button>
                      </div>
                    )}

                    {/* 업체 검색 모달 */}
                    <CompanySearch
                      open={modalOpen}
                      onClose={() => setModalOpen(false)}
                      onSelect={(company) =>
                        setForm({
                          ...form,
                          meta: {
                            ...form.meta,
                            confirmer_company_name: company.name,
                            manualCompanyInput: false,
                          },
                        })
                      }
                    />
                  </div>
                }
              />

              <TableCell label="작성자" value={contract.created_by} />
            </tr>
            {/* 세부공종 / 상태 */}
            <tr>
              {/* <TableCell
                label="세부 공종"
                editing={editing}
                value={TYPE_DETAIL_LABELS[meta.construction_type_detail]}
                input={
                  <select
                    name="construction_type_detail"
                    value={form.meta.construction_type_detail}
                    onChange={handleMetaChange}
                    className="input-base"
                  >
                    {Object.entries(TYPE_DETAIL_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                }
              /> */}
            </tr>
            {/* 특이사항 */}
            <tr className="align-top">
              {/* <TableCell
                label="Revision"
                editing={editing}
                value={`Rev.${meta.revision}`}
                input={
                  <input
                    type="number"
                    name="revision"
                    value={form.meta.revision}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />                 */}
              <TableCell
                label="특이사항"
                editing={editing}
                colSpan={3}
                value={
                  <div className="whitespace-pre-line">{meta.memo || "-"}</div>
                }
                input={
                  <textarea
                    name="memo"
                    value={form.meta.memo}
                    onChange={handleMetaChange}
                    className="input-base h-40 resize-none"
                  />
                }
              />
            </tr>
            <tr>            
            </tr>
            {/* Activity */}
            {/* <tr className="align-top">
              <TableCell
                label="Activity"
                editing={editing}
                colSpan={3}
                value={
                  meta.activity_ids.length
                    ? meta.activity_ids.map((id) => (
                        <div key={id}>Activity #{id}</div>
                      ))
                    : "등록된 Activity 없음"
                }
                input={
                  <div className="space-y-3">
                    <ActivitySearch
                      projectId={contract.project}
                      value={null}
                      onChange={(a) => {
                        if (form.meta.activity_ids.includes(a.id))
                          return alert("이미 선택됨");

                        setForm((prev) => ({
                          ...prev,
                          meta: {
                            ...prev.meta,
                            activity_ids: [...prev.meta.activity_ids, a.id],
                          },
                        }));
                      }}
                    />

                    <div className="mt-2 space-y-1">
                      {form.meta.activity_ids.map((id) => (
                        <div key={id} className="flex items-center gap-2 text-sm">
                          <span>Activity #{id}</span>
                          <button
                            className="text-red-500"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                meta: {
                                  ...prev.meta,
                                  activity_ids: prev.meta.activity_ids.filter(
                                    (x) => x !== id
                                  ),
                                },
                              }))
                            }
                          >
                            삭제
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                }
              />
            </tr> */}
          </tbody>
        </table>
      </div>

      {/* -------------------- 파일 목록 / 업로드 -------------------- */}
      <div className="space-y-6">
        {editing ? (
          <AttachmentBox ref={attachmentRef} initial={loadedFiles} />
        ) : (
          <FileList
            dns={contract.dns}
            onFilesLoaded={(files) => {
              setLoadedFiles(
                files.map((f) => ({
                  _type: "library",
                  id: f.file_id ?? f.id,
                  name: f.name,
                }))
              );
            }}
          />
        )}
      </div>

      {/* ---------------------- 버전 패널 ---------------------- */}
      <SidePanel
        isOpen={showVersionPanel}
        onClose={() => setShowVersionPanel(false)}
        title="수정 내역"
        width={650}
      >
        <div className="p-4">
          <table className="min-w-full text-sm border rounded-lg">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="px-3 py-2 border">Rev</th>
                <th className="px-3 py-2 border">Ver</th>
                <th className="px-3 py-2 border">제목</th>
                <th className="px-3 py-2 border">날짜</th>
                <th className="px-3 py-2 border">상태</th>
              </tr>
            </thead>

            <tbody>
              {versions.length ? (
                versions.map((v) => (
                  <tr
                    key={v.dns}
                    className="hover:bg-blue-50 cursor-pointer"
                    onClick={() => {
                      fetchDetail(v.dns);
                      setEditing(false);
                      setShowVersionPanel(false);
                    }}
                  >
                    <td className="px-3 py-2 border">{v.revision}</td>
                    <td className="px-3 py-2 border">{v.version}</td>
                    <td className="px-3 py-2 border">{v.title}</td>
                    <td className="px-3 py-2 border">
                      {v.created_at
                        ? new Date(v.created_at).toLocaleString("ko-KR")
                        : "-"}
                    </td>
                    <td className="px-3 py-2 border">
                      {v.is_current ? "최신" : "과거"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-gray-500">
                    수정 내역 없음
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SidePanel>
    </div>
  );
};

export default Sub_view;
